# I want to build my own house <span style="color:#CDC4C1"> (0.0.1) </span>
## 2023/09/04

I constantly find myself thinking about my ideal home and living situation, engaging in passionate debates within my own mind about the location, materials, and design of the house I'm going to build. It might sound a bit crazy, but I can spend hours immersed in these thoughts. I decided to document my ideas to prevent them from fading and to continue refining them. It would also be a good way to communicate when I actually build one with Charles.

### Lifestyle
1. **Urban**: Easy access to many interesting people, job opportunities, good restaurants (especially Korean), cultural activities, exotic groceries, and fun hangouts with friends.
2. **Nature**: Easy access to more space, nature, create, farm, grow, meditate, and hike.

I separated them into two criteria because it's very challenging to find both in one place. I am currently living in Brooklyn, which has been well in line with my first criterion so I will now focus on the second lifestyle aspect, 'Nature', in this blog post.

### Basic Requirements
1. **Capital**: A lump sum of money to purchase land and build a house.
2. **Income**: Constant source of income to support any additional costs that may arise during/after the process of building a house.
3. **View**
4. **Space**: It doesn't have to be gigantic, but space for growing vegetables, crafting, and a couple of animals would be nice. (super easy)
5. **Accessible Food & Culture**: It doesn't have to be walkable, but a relatively easy access to groceries and culture, even if it's as small as a farmer's market.
6. **Nature**: An easy access to the nature - and we prefer the forest over the ocean.
7. **Clean water**
8. **Safety**: The place should be safe from human-related crimes and not undergoing any active climate change.
9. **People**: It'd be nice if the people are friendly and easy to communicate with.
10. **Hospital**

### Thinking about a Cozy House
Once this extensive list is satisfied and I find a place, I can then think about the building itself. The following is a rough list of things that I think it'd be nice to have inside and out. The main theme is "well-designed & sustainable". There are three categories that I'd like to consider:
- Sustainable(🌳): Saves excessive use of energy and money
- Design(💅): Primarily for aesthetics
- Functional(🧱): Improves the quality of life

#### Exterior
| Item | Criteria | Details |
|:---:|:---:|:---:|
| **Balcony** | 🌳🧱 | With a balcony and additional sunshade, it will help keep the indoors cool in summer and warm in winter. This is because in the summer, the sun is located higher in the sky, which means it won't significantly heat indoors. But in the winter, the sun is positioned lower, allowing us to enjoy its warmth indoors. It will also give us an easy access to wider views. |
| **Raindrop windows** | 💅🧱 | Charles loves watching rain drops on the window during rainy days. |
| **A-shaped roof** | 🌳💅 | An A-shaped roof is more weather-resistant compared to a flat roof. |
| **Solar panels** | 🌳 |  |
| **Quality materials that age well** | 🌳💅🧱 | |
| **Places to dry clothes & comforter** | 🌳🧱 | It is highly energy-efficient, offers natural disinfection, and provides numerous other beneficial effects. |
| **Sunlight** | 🌳🧱 |  |
| **Bouldering** | 🧱 | Always thought it'd be cool to have one. |
| **Courtyard** | 🌳💅🧱 | A courtyard brings the outside in, enabling people to enjoy the outdoors more privately and freely. It is also known to help keep the interior cooler in hot weather and warmer in cold weather. |
| **Land Heating** | 🌳🧱 | Heard that it really cools down the space without having to use A/C. |
| **Food Garden**  | 🌳🧱 |  |
| **Ceiling window** | 🌳💅🧱 | Ventilation is important in these areas and natural sunlight is always a plus. |
| **Passive House principles** | 🌳🧱 |  |

#### Interior
| Item | Criteria | Details |
|:---:|:---:|:---:|
| **Hardwood floor** | 🌳💅🧱 | Expensive but always good to have hardwood floor. I'm wondering if I could make it myself. |
| **Cross breeze** | 🌳🧱 |  |
| **Tilt-and-turn windows** | 🌳🧱 |  |
| **External shades** | 🌳🧱 | I noticed lots of external shades in Germany. It's a good idea to keep the house cool in summer and warm in winter. |
| **Fire place** | 🌳💅🧱 |  |
| **Thermal heating** | 🌳🧱 |  |
| **Air purifier** | 🌳🧱 |  |
| **HRV** | 🌳🧱 |  |
| **Crafting space** | 🧱 | We always wish to have a dedicated space for crafting, such as wood/metalworking, watchmaking, and drafting. This space could also have an office space if needed for remote work. |
| **Ceiling fans** | 🌳🧱 | Heard that it really cools down the space without having to use A/C. |
| **Windows @ kitchen, bathrooms** | 🌳💅🧱 | Ventilation is important in these areas and natural sunlight is always a plus. |
| **Kitchen island** | 💅🧱 |  |
| **Miele Washer/Dryer/Vacuum** | 💅🧱 | I really liked them. |

![Alt Text alt ><](https://media.giphy.com/media/ianAz6rcKfjoY/giphy.gif)